import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, PencilLine, MessageSquare, Check, X, FileSpreadsheet } from "lucide-react";
import { RequirementImportDialog } from "./importConfigs";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { REQ_STATUS_COLORS, PRIORITY_COLORS, APPROVAL_COLORS } from "./projectUtils";
import RequirementForm from "./RequirementForm";
import { useConfirm } from "../ConfirmProvider";

export default function RequirementsPanel({ project, requirements, users, canEdit, isClient, onChanged }) {
  const confirm = useConfirm();
  const [importOpen, setImportOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [commentDraft, setCommentDraft] = useState({});

  const nameOf = (id) => users.find(u => u.id === id)?.name || "—";

  const remove = async (id) => {
    if (!(await confirm({ title: "Are you sure you want to delete this requirement?", description: "This cannot be undone." }))) return;
    try { await api.delete(`/requirements/${id}`); toast.success("Deleted"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setApproval = async (id, approval_status) => {
    try {
      await api.put(`/requirements/${id}`, { approval_status });
      toast.success(`Marked ${approval_status}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/requirements/${id}`, { status });
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addComment = async (id) => {
    const body = (commentDraft[id] || "").trim();
    if (!body) return;
    try {
      await api.post(`/requirements/${id}/comments`, { body });
      setCommentDraft({ ...commentDraft, [id]: "" });
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Requirements</div>
          <div className="text-neutral-600 text-sm">{requirements.length} items · {requirements.filter(r => r.approval_status === "Approved").length} approved</div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-requirements-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Import from Excel
            </Button>
            <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-requirement-btn" className="bg-orange-500 hover:bg-orange-600 text-white" size="sm">
              <Plus className="w-4 h-4 mr-1" /> New Requirement
            </Button>
          </div>
        )}
        <RequirementImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={project.id} onImported={onChanged} />
      </div>

      {requirements.length === 0 ? (
        <div className="panel p-8 text-center text-neutral-500 text-sm">No requirements captured yet.</div>
      ) : (
        <div className="space-y-3">
          {requirements.map(r => (
            <div key={r.id} className="panel p-4" data-testid={`req-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-neutral-500">{r.requirement_id}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PRIORITY_COLORS[r.priority] || ""}`}>{r.priority}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${REQ_STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${APPROVAL_COLORS[r.approval_status] || APPROVAL_COLORS.Pending}`}>Approval: {r.approval_status}</span>
                  </div>
                  <div className="font-semibold mt-1">{r.title}</div>
                  <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{r.description}</div>
                  <div className="mt-2 flex items-center gap-3 text-[10px] font-mono text-neutral-500 flex-wrap">
                    <span>Category: <span className="text-neutral-700">{r.category}</span></span>
                    <span>Assigned: <span className="text-neutral-700">{nameOf(r.assigned_to)}</span></span>
                    {r.deadline && <span>Due: <span className="text-neutral-700">{r.deadline}</span></span>}
                    {r.attachments?.length > 0 && <span>{r.attachments.length} attachment(s)</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => setExpanded({...expanded, [r.id]: !expanded[r.id]})} data-testid={`req-toggle-${r.id}`}>
                    <MessageSquare className="w-4 h-4 text-neutral-600" />
                  </Button>
                  {canEdit && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => { setEdit(r); setOpen(true); }} data-testid={`req-edit-${r.id}`}>
                        <PencilLine className="w-4 h-4 text-neutral-600" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`req-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-400" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {(canEdit || isClient) && (
                <div className="mt-3 pt-3 border-t border-[#E4E7EC] flex items-center gap-2 flex-wrap">
                  {isClient && r.approval_status !== "Approved" && (
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10" onClick={() => setApproval(r.id, "Approved")} data-testid={`req-approve-${r.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {isClient && r.approval_status !== "Rejected" && (
                    <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10" onClick={() => setApproval(r.id, "Rejected")} data-testid={`req-reject-${r.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Request Changes
                    </Button>
                  )}
                  {canEdit && (
                    <select
                      className="text-[11px] font-mono bg-[#FFFFFF] border border-[#E4E7EC] rounded px-2 py-1"
                      value={r.status}
                      onChange={e => setStatus(r.id, e.target.value)}
                      data-testid={`req-status-select-${r.id}`}>
                      {["Draft", "Under Review", "Approved", "Rejected", "Completed"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </div>
              )}

              {expanded[r.id] && (
                <div className="mt-3 pt-3 border-t border-[#E4E7EC] space-y-2">
                  {(r.comments || []).length === 0 && <div className="text-[11px] font-mono text-neutral-500">No comments yet.</div>}
                  {(r.comments || []).map(c => (
                    <div key={c.id} className="text-xs bg-[#FFFFFF] rounded p-2 border border-[#E4E7EC]">
                      <div className="text-[10px] font-mono text-neutral-500 mb-1">{c.author_name} <span className="text-neutral-600">· {c.author_role}</span></div>
                      <div className="text-neutral-800">{c.body}</div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Textarea rows={1} value={commentDraft[r.id] || ""} onChange={e => setCommentDraft({...commentDraft, [r.id]: e.target.value})} placeholder="Add a comment..." className="bg-[#FFFFFF] border-[#E4E7EC] text-xs" data-testid={`req-comment-input-${r.id}`} />
                    <Button size="sm" onClick={() => addComment(r.id)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`req-comment-post-${r.id}`}>Post</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <RequirementForm open={open} onOpenChange={setOpen} initial={edit} projectId={project.id} users={users} onSaved={onChanged} />
    </div>
  );
}
